import {
  CARDS,
  CARDS_DATA,
  DEVICES,
  EQUIPMENT,
  SPECIALISTS,
  TECHS,
  TOWERS,
  TOWER_SKILLS,
  UNIT_RECORDS,
} from '@mechrulez/data'
import { CARDS as DISPLAY_CARDS } from '../game/data/cards.js'
import {
  UNIT_PRESENTATIONS,
  UNIT_PRESENTATIONS_BY_ID,
  type Locale,
  type LocalizedText,
  type UnitPalette,
  type UnitPresentationRecord,
} from './catalog.js'

export type { Locale, LocalizedText, UnitPalette, UnitPresentationRecord }
export { UNIT_PRESENTATIONS, UNIT_PRESENTATIONS_BY_ID }

export const BRAND_COLORS = Object.freeze({ graphite: '#151B22', ceramic: '#E6E3D9', orange: '#FF6A32' })
export const BRAND_ASSETS = Object.freeze({
  logo: '/brand/mecharulez-logo.svg',
  wordmark: '/brand/mecharulez-wordmark.svg',
  lockup: '/brand/mecharulez-lockup.svg',
  icon: '/brand/mecharulez-icon.svg',
  favicon: '/brand/favicon.svg',
})

const STORAGE_KEY = 'mecharulez.locale'
const listeners = new Set<(next: Locale) => void>()

function savedLocale(): Locale {
  try {
    const value = globalThis.localStorage?.getItem(STORAGE_KEY)
    return value === 'ru' ? 'ru' : 'en'
  } catch {
    return 'en'
  }
}

/** Live ESM binding. English is the default; a valid saved user choice wins in browsers. */
export let locale: Locale = savedLocale()

export function getLocale(): Locale {
  return locale
}

export function setLocale(next: Locale): void {
  if (next !== 'en' && next !== 'ru') return
  if (locale === next) return
  locale = next
  try { globalThis.localStorage?.setItem(STORAGE_KEY, next) } catch { /* storage may be unavailable */ }
  if (typeof window !== 'undefined') {
    try {
      const url = new URL(window.location.href)
      if (/^\/play(?:\/|$)/.test(url.pathname) || url.searchParams.has('seed') || url.searchParams.has('mode')) {
        url.searchParams.set('lang', next)
        window.history.replaceState(window.history.state, '', url)
      }
    } catch { /* isolated embeds may restrict history access */ }
  }
  if (typeof document !== 'undefined') document.documentElement.lang = next
  for (const listener of listeners) listener(next)
}

export function subscribeLocale(listener: (next: Locale) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export interface UnitPresentation {
  id: number
  slug: string
  name: string
  role: string
  description: string
  visual: string
  palette: UnitPalette
}

export function unitPresentation(id: number, language: Locale = locale): UnitPresentation | undefined {
  const entry = UNIT_PRESENTATIONS_BY_ID[id]
  return entry && { id: entry.id, slug: entry.slug, name: entry.name[language], role: entry.role[language], description: entry.description[language], visual: entry.visual[language], palette: entry.palette }
}

export function unitName(id: number, language: Locale = locale): string {
  return UNIT_PRESENTATIONS_BY_ID[id]?.name[language] ?? `Unit ${id}`
}

export type EntityKind =
  | 'unit' | 'specialist' | 'tech' | 'card' | 'equipment' | 'device' | 'tower' | 'towerSkill'

interface SourceEntity { id: number | string; aliases?: string[]; en: string; ru: string; descriptions?: LocalizedText[] }

const lt = (en: string, ru: string): LocalizedText => ({ en, ru })

/** Original league-facing names for high-visibility public systems. */
const NAME_OVERRIDES: Partial<Record<EntityKind, Readonly<Record<string, LocalizedText>>>> = {
  specialist: {
    '10002': lt('Supply Marshal', 'Маршал снабжения'), '10010': lt('Launch Quartermaster', 'Стартовый интендант'),
    '10011': lt('Rocket Director', 'Директор ракетных систем'), '10013': lt('Overdrive Coach', 'Тренер форсажа'),
    '10014': lt('Drillmaster', 'Главный инструктор'), '20005': lt('Titan Broker', 'Куратор титанов'),
    '20021': lt('Flight Commissioner', 'Авиационный комиссар'), '20024': lt('Pace Setter', 'Темповик'),
    '20029': lt('Longshot Handler', 'Куратор «Дальнобоя»'), '20032': lt('Premier Scout', 'Скаут высшей лиги'),
    '20033': lt('Breakpress Handler', 'Куратор «Прессбоя»'), '20034': lt('Budget Controller', 'Контролёр бюджета'),
    '20035': lt('Armor Steward', 'Распорядитель брони'), '20036': lt('Ironjaw Handler', 'Куратор «Железочелюсти»'),
    '20038': lt('Kilnrunner Handler', 'Куратор «Печебега»'), '20039': lt('Galeguard Handler', 'Куратор «Штормстража»'),
  },
  device: {
    '10001': lt('Aegis Projector', 'Проектор «Эгида»'), '20001': lt('Proximity Lance', 'Контактное копьё'),
    '30001': lt('Flak Relay', 'Рубеж ПВО'),
  },
  tower: {
    command: lt('League Control', 'Управление лиги'), research: lt('Pit Lab', 'Лаборатория пит-стопа'),
  },
  towerSkill: {
    'command:1': lt('Cash Injection', 'Срочное финансирование'), 'command:3': lt('Open Roster', 'Расширение состава'),
    'command:4': lt('Seeded Entrants', 'Сеянные участники'), 'command:5': lt('Range Window', 'Окно дальности'),
    'command:6': lt('Pace Lap', 'Темповый круг'), 'research:1': lt('Grip-Slick Canister', 'Баллон скользкого состава'),
    'research:2': lt('Scrap Dividend', 'Премия за утилизацию'), 'research:3': lt('Route Pylon', 'Маршрутный пилон'),
    'research:4': lt('Power Tune I', 'Настройка мощности I'), 'research:401': lt('Power Tune II', 'Настройка мощности II'),
    'research:5': lt('Armor Tune I', 'Настройка брони I'), 'research:501': lt('Armor Tune II', 'Настройка брони II'),
  },
  card: {
    '100002': lt('Fireline Charge', 'Заряд огневой линии'), '200001': lt('Blackout Drop', 'Импульс отключения'),
    '200002': lt('Blackout Field', 'Поле отключения'), '200003': lt('Radiant Screen', 'Световой заслон'),
    '300001': lt('Guided Impact', 'Управляемый удар'), '300003': lt('High-Arc Salvo', 'Высотный залп'),
    '300004': lt('Final Bell', 'Финальный гонг'), '300005': lt('Storm Grid', 'Грозовая сетка'),
    '300006': lt('Ion Rake', 'Ионный прочёс'), '300007': lt('Skyspike', 'Небесный шип'),
    '500002': lt('Corrosive Lane', 'Коррозионная полоса'), '600002': lt('Blind Sector', 'Слепой сектор'),
    '800001': lt('Aegis Drop', 'Сброс «Эгиды»'), '1000001': lt('Formation Reset', 'Перестройка'),
    '1100001': lt('Full Drill', 'Полный тренинг'), '1200001': lt('Subsurface Entry', 'Подземный выход'),
    '1200002': lt('Breakpress Entry', 'Выход «Прессбоя»'), '1200003': lt('Needlewing Flight', 'Звено «Иглокрыла»'),
    '1200004': lt('Skyhold Entry', 'Выход «Небодержа»'), '1200005': lt('Cinderwall Entry', 'Выход «Шлакостена»'),
    '1500002': lt('Route Pylon', 'Маршрутный пилон'), '10003': lt('League Dividend', 'Дивиденд лиги'),
    '10004': lt('Extra Roster Slot', 'Дополнительное место состава'), '10007': lt('Reinforced Aegis', 'Усиленная «Эгида»'),
    '10008': lt('Tuned Proximity Lance', 'Настроенное контактное копьё'), '10009': lt('Fast Flank Gate', 'Быстрые фланговые ворота'),
    '20001': lt('Armor Playbook', 'Броневая схема'), '20002': lt('Power Playbook', 'Силовая схема'),
    '20003': lt('Efficient Engineering', 'Экономная инженерия'), '20004': lt('Drive Tune', 'Настройка привода'),
    '20006': lt('Targeting Tune', 'Настройка наведения'), '20007': lt('Supply Dividend', 'Дивиденд снабжения'),
    '20022': lt('Titan Contract', 'Контракт титанов'), '20023': lt('Lightweight Contract', 'Контракт лёгкого класса'),
  },
  equipment: {
    '1305003': lt('Radiant Sheath', 'Световая оболочка'), '1305004': lt('Blackout Rounds', 'Отключающие снаряды'),
    '1305005': lt('Seeder Rounds', 'Сеющие снаряды'), '1305006': lt('Pinning Rounds', 'Фиксирующие снаряды'),
    '1305007': lt('Adaptive Combat Core', 'Адаптивное боевое ядро'), '1305008': lt('Radiant Emitter', 'Световой излучатель'),
    '1305009': lt('Corrosive Rounds', 'Коррозионные снаряды'), '1305010': lt('Vector Radar', 'Векторный радар'),
    '1306001': lt('Assembly Gantry', 'Сборочный портал'),
    '1307001': lt('Aegis Frame', 'Каркас «Эгида»'), '1307002': lt('Aegis Frame XL', 'Каркас «Эгида» XL'),
    '1308001': lt('Signal Guard', 'Защита сигнала'), '1309001': lt('Recovery Loop', 'Контур восстановления'),
    '13010001': lt('Pack Shield', 'Ранцевый щит'), '13020001': lt('Nanoforge Kit', 'Комплект нанокузни'),
    '13030001': lt('Beam Calibrator', 'Лучевой калибратор'), '13030002': lt('Slab Armor', 'Плитная броня'),
    '13030003': lt('Fire Director', 'Огневой вычислитель'), '13030004': lt('Tune Module', 'Модуль настройки'),
    '13030005': lt('Sprint Governor', 'Регулятор рывка'), '13030006': lt('Titan Slab Armor', 'Титаническая плитная броня'),
    '13030007': lt('Overdrive Core', 'Ядро форсажа'), '13030009': lt('Compact Overdrive Core', 'Компактное ядро форсажа'),
    '13030010': lt('Last-Stand Core', 'Ядро последнего рубежа'), '13030011': lt('Cycle Loader', 'Цикловой автомат заряжания'),
    '13040001': lt('Formation Coupler', 'Соединитель построения'), '13100001': lt('Monolith Test Plates', 'Испытательные плиты «Монолита»'),
    '13110001': lt('Fork Core', 'Разделяющее ядро'), '13120001': lt('Reserve Capacitor', 'Резервный конденсатор'),
    '13130001': lt('Light-Bend Shroud', 'Светопреломляющий кожух'), '13140001': lt('Titan Fire Director', 'Титанический огневой вычислитель'),
    '13150001': lt('Breach Shells', 'Пробивные снаряды'),
  },
}

const TECH_NAME_OVERRIDES: Readonly<Record<string, LocalizedText>> = {
  'Barrier': lt('Bulwark Dome', 'Купол бастиона'), 'Anti-Air Barrage': lt('Skybreak Salvo', 'Залп неболома'),
  'Fang Production': lt('Picket Assembly', 'Сборка «Дозора»'), 'Range Enhancement': lt('Long-Caliber Tune', 'Настройка длинного калибра'),
  'Launcher Overload': lt('Rack Overdrive', 'Форсаж пусковых кассет'), 'Elite Marksman': lt('Prime Ballistics', 'Высшая баллистика'),
  'Doubleshot': lt('Twin Cycle', 'Парный цикл'), 'Shooting Squad': lt('Fireteam Protocol', 'Протокол огневой группы'),
  'Electromagnetic Shot': lt('Blackout Round', 'Отключающий снаряд'), 'Assault Mode': lt('Breach Stance', 'Стойка прорыва'),
  'Quick Reload': lt('Cycle Cut', 'Сокращённый цикл'), 'Incendiary Bomb': lt('Fireline Canister', 'Баллон огневой линии'),
  'Best Partner': lt('Paired Drive', 'Парный привод'), 'Armor Enhancement': lt('Plate Tempering', 'Закалка плит'),
  'Scorching Fire': lt('Kiln Flame', 'Печное пламя'), 'Sticky Oil Bomb': lt('Grip-Slick Canister', 'Баллон скользкого состава'),
  'Ignite': lt('Thermal Mark', 'Тепловая метка'), 'Energy Absorption': lt('Flux Recovery', 'Возврат потока'),
  'Electromagnetic Barrage': lt('Blackout Salvo', 'Отключающий залп'), 'Energy Diffraction': lt('Flux Splitter', 'Расщепитель потока'),
  'Crawler Production': lt('Scrapper Assembly', 'Сборка «Скребней»'), 'Field Maintenance': lt('Pit-Service Loop', 'Контур пит-обслуживания'),
  'Whirlwind': lt('Rotor Surge', 'Роторный натиск'), 'Wreckage Recycling': lt('Scrap Dividend', 'Премия за лом'),
  'Power Armor': lt('Driven Plate', 'Приводная броня'), 'Final Blitz': lt('Last-Lap Charge', 'Рывок последнего круга'),
  'Mechanical Rage': lt('Redline Governor', 'Регулятор красной зоны'), 'Photon Coating': lt('Radiant Sheath', 'Световая оболочка'),
  'Energy Shield': lt('Flux Guard', 'Потоковый заслон'), 'Ground Specialization': lt('Track Discipline', 'Наземная дисциплина'),
  'Jump Drive': lt('Vault Thrusters', 'Прыжковые ускорители'), 'High-Explosive Ammo': lt('Breachburst Shells', 'Пробивные фугасы'),
  'Aerial Specialization': lt('Flight Discipline', 'Лётная дисциплина'), 'Missile Interceptor': lt('Flak Relay', 'Рубеж ПВО'),
  'Culling Rounds': lt('Finisher Rounds', 'Добивающие снаряды'), 'Armor-Piercing Bullets': lt('Platecut Rounds', 'Бронерезные снаряды'),
  'Damage Sharing': lt('Load-Balance Link', 'Связь распределения нагрузки'), 'Mechanical Division': lt('Split-Chassis Routine', 'Протокол разделения шасси'),
  'Fortified Target Lock': lt('Hardpoint Lock', 'Захват опорной цели'), 'Kinetic Charge': lt('Momentum Bank', 'Накопитель импульса'),
  'Portable Shield': lt('Pack Aegis', 'Ранцевая «Эгида»'), 'Grenade Launcher': lt('Arc Lobber', 'Навесной метатель'),
  'Subterranean Blitz': lt('Undercut Rush', 'Подземный рывок'), 'Acidic Explosion': lt('Corrosive Burst', 'Коррозионный выброс'),
  'Loose Formation': lt('Open Spacing', 'Разомкнутый строй'), 'Impact Drill': lt('Percussion Bore', 'Ударный проходчик'),
  'Replicate': lt('Echo Fabrication', 'Эхо-сборка'), 'Overlord Artillery': lt('Skyhold Fire Support', 'Огневая поддержка «Небодержа»'),
  'Mothership': lt('Carrier Foundry', 'Бортовая фабрика'), 'Photon Emission': lt('Radiant Screen', 'Световой заслон'),
  'Electromagnetic Explosion': lt('Blackout Burst', 'Импульс отключения'), 'Heavy Missile': lt('Siege Spear', 'Осадное копьё'),
  'Enhanced Control': lt('Deep Override', 'Глубокий перехват'), 'Electromagnetic Interference': lt('Signal Jam', 'Глушение сигнала'),
  'Multi Control': lt('Wide Override', 'Широкий перехват'), 'Anti-Aircraft Ammunition': lt('Skycut Rounds', 'Неборезные снаряды'),
  'Shockwave': lt('Impact Ring', 'Ударное кольцо'), 'Charged Shot': lt('Capacitor Round', 'Конденсаторный выстрел'),
  'Quantum Reassembly': lt('Phase Rebuild', 'Фазовая пересборка'), 'Phoenix Production': lt('Ashwing Assembly', 'Сборка «Пеплокрылов»'),
  'Steel Ball Production': lt('Kinetix Assembly', 'Сборка «Кинетиков»'), 'Sledgehammer Production': lt('Anvil Assembly', 'Сборка «Наковален»'),
  'Floating Artillery Array': lt('Hovergun Lattice', 'Решётка парящих орудий'), 'Degeneration Beam': lt('Erosion Lance', 'Луч эрозии'),
  'Siege Mode': lt('Anchor Stance', 'Якорная стойка'), 'Convergent Fire': lt('Crosspoint Fire', 'Огонь в точку сведения'),
  'Acid Attack': lt('Corrosive Payload', 'Коррозионный заряд'), 'Napalm': lt('Gel Fire', 'Гелевый огонь'),
  'Scorching Charge': lt('Kiln Rush', 'Печной рывок'), 'Counter-Fire': lt('Return-Fire Logic', 'Логика ответного огня'),
  'Field Entrenchment': lt('Arena Anchors', 'Аренные анкеры'), 'Secondary Armament': lt('Auxiliary Rack', 'Вспомогательная батарея'),
  'Field Reassembly': lt('Pit-Lane Rebuild', 'Полевая пересборка'), 'Reactive Armor': lt('Impulse Plates', 'Импульсные плиты'),
  'Maintenance Array': lt('Service Lattice', 'Сервисная решётка'), 'Wreckage Detonation': lt('Scrapburst Charge', 'Подрыв лома'),
  'Air Defense Mark': lt('Skybreak Designator', 'Маркер неболома'), 'Burrow Maintenance': lt('Subsurface Service', 'Подземный сервис'),
  'Anti-Aerial': lt('Skyguard Suite', 'Комплекс небозащиты'), 'Sandstorm': lt('Abrasive Screen', 'Абразивная завеса'),
  'Spider Mine': lt('Latch Mine', 'Скобочная мина'), 'Burst Mode': lt('Volley Cycle', 'Залповый цикл'),
  'Stealth Cloak': lt('Light-Bend Shroud', 'Светопреломляющий кожух'), 'Scanning Radar': lt('Sweep Array', 'Обзорная решётка'),
  'Chain': lt('Cascade Link', 'Каскадная связь'), 'Ionization': lt('Ion Flood', 'Ионное насыщение'),
  'Fork': lt('Branch Arc', 'Разветвлённая дуга'), 'Fire Extinguisher': lt('Thermal Purge', 'Тепловая продувка'),
  'Chamber Compression': lt('Breech Compression', 'Сжатие казённика'), 'Vertical Sweep': lt('Altitude Rake', 'Высотный прочёс'),
  'Disintegration': lt('Matter Shear', 'Сдвиг материи'), 'Dark Companion': lt('Shadow Escort', 'Теневой эскорт'),
  'Swarm Missiles': lt('Cloudrack Missiles', 'Ракеты облачной кассеты'), 'Aerial Mode': lt('Lift Configuration', 'Полётная конфигурация'),
  'Suppression Shots': lt('Range-Pinning Fire', 'Огонь подавления дальности'), 'Grid Integration': lt('Lattice Sync', 'Синхронизация решётки'),
  'Electromagnetic Cloud': lt('Blackout Cloud', 'Облако отключения'), 'Electromagnetic Twin': lt('Signal Echo', 'Сигнальное эхо'),
  'Mobile Power Station': lt('Marching Dynamo', 'Походная динамо-машина'), 'Accumulator Shield': lt('Chargebank Guard', 'Щит накопителя'),
  'Emergency Armor': lt('Failsafe Plating', 'Аварийные плиты'), 'Saturation Bombardment': lt('Full-Grid Barrage', 'Залп по всей сетке'),
  'Mountain Plating': lt('Monolith Plates', 'Плиты «Монолита»'), 'Extended Range Ammo': lt('Long-Arc Shells', 'Снаряды дальней дуги'),
  'Gun-launched Missile': lt('Barrel Spear', 'Ствольное копьё'), 'Smoke Bomb': lt('Blindfield Canister', 'Баллон слепого поля'),
  'Photon Loop': lt('Radiant Cycle', 'Световой цикл'),
}

const sourceEntities: Record<EntityKind, SourceEntity[]> = {
  unit: Object.values(UNIT_RECORDS).map((u) => ({ id: u.id, aliases: [u.nameEn, u.nameRu], en: u.nameEn, ru: u.nameRu })),
  specialist: SPECIALISTS.map((s) => ({ id: s.id, aliases: [s.nameEn, s.nameRu], en: s.nameEn, ru: s.nameRu, descriptions: [
    { en: s.descriptionEn, ru: s.descriptionRu }, { en: s.effectTextEn, ru: s.effectTextRu },
  ] })),
  tech: TECHS.map((t) => ({ id: t.id, aliases: [t.name, t.nameRu], en: t.name, ru: t.nameRu, descriptions: [{ en: t.descriptionEn, ru: t.descriptionRu }] })),
  card: [...CARDS, ...CARDS_DATA.skipCards].map((c) => ({ id: c.id, aliases: ['table' in c ? `${c.table}:${c.id}` : `skip:${c.id}`, c.nameEn, c.nameRu], en: c.nameEn, ru: c.nameRu, descriptions: [{ en: c.descriptionEn, ru: c.descriptionRu }] })),
  equipment: EQUIPMENT.map((e) => ({ id: e.id, aliases: [`${e.table}:${e.id}`, e.nameEn, e.nameRu], en: e.nameEn, ru: e.nameRu, descriptions: [{ en: e.descriptionEn, ru: e.descriptionRu }] })),
  device: DEVICES.map((d) => ({ id: d.id, aliases: [`${d.table}:${d.id}`, d.nameEn, d.nameRu], en: d.nameEn, ru: d.nameRu, descriptions: [
    { en: d.descriptionEn, ru: d.descriptionRu }, { en: d.tooltipEn, ru: d.tooltipRu },
  ] })),
  tower: TOWERS.towers.map((tower) => ({ id: tower.kind, aliases: [tower.nameEn, tower.nameRu], en: tower.nameEn, ru: tower.nameRu, descriptions: tower.effectTip ? [tower.effectTip] : [] })),
  towerSkill: TOWER_SKILLS.map((skill) => {
    const purchase = TOWERS.towers.find((tower) => tower.kind === skill.tower)?.purchases.find((item) => item.id === skill.id)
    return { id: skill.id, aliases: [`${skill.tower}:${skill.id}`, skill.nameEn, skill.nameRu], en: skill.nameEn, ru: skill.nameRu, descriptions: purchase ? [
      { en: purchase.descriptionEn, ru: purchase.descriptionRu }, { en: purchase.effectTextEn, ru: purchase.effectTextRu },
    ] : [] }
  }),
}

const entityMaps = Object.fromEntries(Object.entries(sourceEntities).map(([kind, rows]) => {
  const map = new Map<string, SourceEntity>()
  for (const row of rows) {
    if (!map.has(String(row.id))) map.set(String(row.id), row)
    for (const alias of row.aliases ?? []) map.set(alias, row)
  }
  return [kind, map]
})) as Record<EntityKind, Map<string, SourceEntity>>

function overrideFor(kind: EntityKind, lookup: string, row?: SourceEntity): LocalizedText | undefined {
  if (kind === 'tech' && row) return TECH_NAME_OVERRIDES[row.en] ?? TECH_NAME_OVERRIDES[row.ru]
  const overrides = NAME_OVERRIDES[kind]
  if (!overrides) return undefined
  return overrides[lookup] ?? overrides[String(row?.id)] ?? row?.aliases?.map((alias) => overrides[alias]).find(Boolean)
}

/** Name lookup for every public presentation entity. Unit IDs resolve to the original MechaRulez identities. */
export function entityName(kind: EntityKind, id: number | string, fallback = '', language: Locale = locale): string {
  if (kind === 'unit') {
    const numericId = Number(id)
    const sourceId = entityMaps.unit.get(String(id))?.id
    const resolvedId = Number.isFinite(numericId) ? numericId : Number(sourceId)
    return UNIT_PRESENTATIONS_BY_ID[resolvedId]?.name[language] ?? (fallback ? localizeText(fallback, language) : `Unit ${id}`)
  }
  const row = entityMaps[kind].get(String(id))
  const sharedEquipment = kind === 'card' ? overrideFor('equipment', String(id), entityMaps.equipment.get(String(id))) : undefined
  const value = (overrideFor(kind, String(id), row) ?? sharedEquipment)?.[language] ?? row?.[language]
  return value ? replaceUnitNames(value, language) : (fallback ? localizeText(fallback, language) : String(id))
}

const exactText = new Map<string, LocalizedText>()
for (const rows of Object.values(sourceEntities)) {
  for (const row of rows) {
    exactText.set(row.en, { en: row.en, ru: row.ru })
    exactText.set(row.ru, { en: row.en, ru: row.ru })
    for (const description of row.descriptions ?? []) {
      exactText.set(description.en, description)
      exactText.set(description.ru, description)
    }
  }
}

const DISPLAY_CARD_EN: Readonly<Record<number, string>> = {
  10007: 'Aegis Projector durability is increased.', 10008: 'Proximity Lance damage is increased by 200%.',
  10009: 'Flank teleport time is reduced.', 1305003: 'The squad takes less damage early in battle and is immune to EMP, ignite, acid, and degeneration.',
  1308001: 'Immune to EMP, control capture, and core-building paralysis.', 13030005: 'Speed +5, ATK +35%.',
  13040001: 'The squad can be moved during every deployment phase.', 1500002: 'Sets a movement route for the selected squad.',
  200001: 'Deals 20 000 damage to shields, disables tech, and reduces speed by 40% for 25 s.',
  300001: 'A guided missile deals 3 000 damage.', 800001: 'Drops an energy shield with 50 000 HP.',
  1000001: 'Move the selected squad one additional time during this deployment phase.', 1100001: 'Fills the selected squad’s experience to maximum.',
  1200001: 'Scrapper squads continue to emerge at the marked point.', 1200002: 'Deploys Breakpress (1).',
  1200003: 'Calls in a Needlewing flight.', 13030001: 'Attack range +20 m (ranged units only).', 13030002: 'HP +75%.',
  13030003: 'ATK is increased.', 10004: 'Units deployable per round +1.', 20003: 'Tech installation cost for all units −50.',
  20004: 'Movement speed of all units is increased.', 20007: 'Gain 50 additional supplies every round.',
  20022: 'Titan recruitment cost −100.', 100002: 'Fire wall dealing 270 damage per second.',
  200003: 'Allies in the area take 30% less damage for 20 s.', 300003: 'Fires a missile salvo into the target area.',
  500002: 'Acid lane: −3% HP per second and +250% damage taken; lasts 1 round.', 600002: 'Smoke lane that reduces attack range inside it.',
  1307001: '[Ground titan] Deploys a barrier that protects nearby units.', 1309001: 'HP is increased and part of damage dealt returns as HP.',
  13010001: 'A shield absorbs damage equal to the unit’s HP.', 13020001: 'Restores a percentage of maximum HP each second.',
  13030007: 'ATK +50%, HP +50%.', 13030010: 'ATK and HP are increased; +50 supplies per round. Losing the carrier destroys the entire army.',
  10003: 'Starting next round, gain 100 additional supplies every round.', 20001: 'HP of all units is increased.',
  20002: 'ATK of all units is increased.', 20006: 'Attack range of all ranged units is increased.',
  20023: 'Recruitment cost of all non-titan units −50.', 200002: 'Creates a large-area electromagnetic pulse.',
  300004: 'Deals 70 000 damage after 15 s.', 300005: 'Deals 2 000 damage and reduces speed by 65% for 20 s.',
  300006: 'A slow beam that deals continuous damage.', 300007: 'Deals 70 000 damage within 30 m and cannot be stopped by energy shields.',
  1200004: 'Calls in Skyhold (1).', 1200005: 'Deploys a Cinderwall.', 1306001: '[Titan] Produces Anvil squads in battle.',
  1306002: '[Titan] Produces Ripsaw squads in battle.', 1306003: '[Titan] Produces Kinetix squads in battle.',
  13030004: 'ATK +25%, HP +25%, promotion cost −100.', 13030006: 'HP +150%.',
}

// The playable-card layer resolves source templates and a few verified summaries before display.
// Index those rendered strings too, so switching to EN never leaks Russian or drops their values.
for (const card of DISPLAY_CARDS) {
  const pair = {
    en: DISPLAY_CARD_EN[card.id] ?? card.record.descriptionEn,
    ru: card.descriptionRu,
  }
  exactText.set(card.descriptionRu, pair)
  exactText.set(pair.en, pair)
}

const UI_TEXT_PAIRS = [
  lt('Battlefield abilities — unavailable in this version', 'Полевые способности — недоступно в этой версии'),
  lt('Equipment — unavailable in this version', 'Снаряжение — недоступно в этой версии'),
  lt('unavailable in this version', 'недоступно в этой версии'),
  lt('Shield {0} %', 'Щит {0} %'), lt('Ignited', 'Поджог'), lt('Acid', 'Кислота'), lt('EMP', 'ЭМИ'),
  lt('Cloaked', 'Маскировка'), lt('Burrowed', 'Подкоп'), lt('Produced in battle', 'Произведён в бою'),
  lt('{0} (this round)', '{0} (этот раунд)'),
  lt('Attack range {0}', 'Радиус атаки {0}'), lt('Minimum range {0}', 'Минимальная дальность {0}'),
  lt('Air attack range {0}', 'Дальность по воздуху {0}'), lt('Ground attack range {0}', 'Дальность по земле {0}'),
  lt('Attack interval {0}', 'Интервал атаки {0}'), lt('ATK {0}', 'АТК {0}'), lt('HP {0}', 'ОЗ {0}'),
  lt('Blocked damage {0}', 'Блокируемый урон {0}'), lt('Ranged damage {0}', 'Урон с дистанции {0}'),
  lt('Damage taken {0}', 'Получаемый урон {0}'), lt('Damage amplification {0}', 'Усиление урона {0}'),
  lt('Speed {0}', 'Скорость {0}'), lt('Splash {0}', 'Разлет осколков {0}'),
  lt('Projectiles per shot {0}', 'Снарядов за выстрел {0}'), lt('Projectile speed {0}', 'Скорость снаряда {0}'),
  lt('Projectile lifetime {0}', 'Время жизни снаряда {0}'), lt('Anti-air attack {0}', 'Атака по воздуху {0}'),
  lt('Ground attack {0}', 'Атака по земле {0}'), lt('HP recovery {0}', 'Восстановление ОЗ {0}'),
  lt('Target-HP damage {0}', 'Урон от ОЗ цели {0}'), lt('HP per kill {0}', 'ОЗ за убийство {0}'), lt('ATK per kill {0}', 'АТК за убийство {0}'),
] as const

for (const pair of UI_TEXT_PAIRS) {
  exactText.set(pair.en, pair)
  exactText.set(pair.ru, pair)
}

for (const [sourceEn, sourceRu, pair] of [
  ['Get 2 Missile Strike for free on round 2', 'Получите 2 Ракетный удар бесплатно в раунде 2', lt('Receive 2 Guided Impact uses free in round 2.', 'Получите 2 бесплатных применения «Управляемого удара» в раунде 2.')],
  ['Get 3 Small Amplifying Core for free on round 1', 'Получите 3 Малое усилительное ядро бесплатно в раунде 1', lt('Receive 3 Compact Overdrive Cores free in round 1.', 'Получите 3 компактных ядра форсажа бесплатно в раунде 1.')],
  ['Obtain extra 50 supplies in the first round, and get 1 Intensive Training for free.', 'Получите дополнительные 50 принадлежности в первом раунде и получите 1 Интенсивное обучение бесплатно.', lt('Gain 50 extra supplies in the first round and receive 1 Full Drill free.', 'Получите 50 дополнительных припасов и 1 бесплатный «Полный тренинг» в первом раунде.')],
  ['Get 1 Rank 3 Marksman(s) for free on round 2', 'Получите Снайперов (1 шт.) ранга 3 бесплатно в раунде 2', lt('Receive 1 rank 3 Longshot squad free in round 2.', 'Получите 1 отряд «Дальнобоя» ранга 3 бесплатно в раунде 2.')],
  ['Get 1 Rank 2 Rhino(s) for free on round 4', 'Получите Носорога (1 шт.) ранга 2 бесплатно в раунде 4.', lt('Receive 1 rank 2 Breakpress squad free in round 4.', 'Получите 1 отряд «Прессбоя» ранга 2 бесплатно в раунде 4.')],
  ['Decreases Sabertooth’s Tech Upgrade cost by 50. Get 1 Rank 1 Sabertooth on Round 3.', 'Снижает стоимость технического обновления Саблезуб на 50. Получите 1 ранг 1 Саблезуб в раунде 3.', lt('Ironjaw tech costs 50 less. Receive 1 rank 1 Ironjaw squad in round 3.', 'Технологии «Железочелюсти» стоят на 50 меньше. Получите 1 отряд ранга 1 в раунде 3.')],
  ['Decreases Fire Badger’s Tech Upgrade cost by 50. Get 1 Rank 1 Fire Badger on Round 3.', 'Снижает стоимость технического обновления «Огненного барсука» на 50. Получите 1 ранг 1 «Огненный барсук» в раунде 3.', lt('Kilnrunner tech costs 50 less. Receive 1 rank 1 Kilnrunner squad in round 3.', 'Технологии «Печебега» стоят на 50 меньше. Получите 1 отряд ранга 1 в раунде 3.')],
  ['Get 1 Rank 1 Typhoon(s) for free on round 4', 'Получите Тайфун (1 шт.) ранга 1 бесплатно в раунде 4', lt('Receive 1 rank 1 Galeguard squad free in round 4.', 'Получите 1 отряд «Штормстража» ранга 1 бесплатно в раунде 4.')],
] as const) {
  exactText.set(sourceEn, pair)
  exactText.set(sourceRu, pair)
}

const descriptionTemplates = Object.values(sourceEntities)
  .flatMap((rows) => rows.flatMap((row) => row.descriptions ?? []))
  .concat(UI_TEXT_PAIRS)
  .filter((pair) => pair.en.includes('{') || pair.ru.includes('{'))

function fromTemplate(text: string, source: string, target: string): string | undefined {
  const markers = [...source.matchAll(/\{(\d+)\}/g)]
  if (markers.length === 0) return undefined
  let cursor = 0
  let pattern = '^'
  for (const marker of markers) {
    pattern += escapeRegExp(source.slice(cursor, marker.index)) + '([\\s\\S]+?)'
    cursor = marker.index! + marker[0].length
  }
  pattern += escapeRegExp(source.slice(cursor)) + '$'
  const match = text.match(new RegExp(pattern, 'iu'))
  if (!match) return undefined
  const params = new Map<string, string>()
  markers.forEach((marker, index) => { if (!params.has(marker[1]!)) params.set(marker[1]!, match[index + 1]!) })
  return target.replace(/\{(\d+)\}/g, (token, index: string) => params.get(index) ?? token)
}

function localizeTemplate(text: string, language: Locale): string | undefined {
  for (const pair of descriptionTemplates) {
    const translated = fromTemplate(text, pair.en, pair[language]) ?? fromTemplate(text, pair.ru, pair[language])
    if (translated !== undefined) return translated
  }
  return undefined
}

const RU_UNIT_ALIASES: Readonly<Record<number, readonly string[]>> = {
  1: ['Крепость', 'Крепости', 'Крепостью'], 2: ['Снайпер', 'Снайпера', 'Снайперы', 'Снайперов', 'Снайперам', 'Снайперами'],
  3: ['Вулкан', 'Вулкана', 'Вулканом', 'Вулканы'], 4: ['Плавильня', 'Плавильни', 'Плавильню', 'Плавильней'],
  5: ['Носорог', 'Носорога', 'Носороги', 'Носорогов', 'Носорогам'], 6: ['Оса', 'Осы', 'Ос', 'Осами'],
  7: ['Мустанг', 'Мустанга', 'Мустанги', 'Мустангов'], 8: ['Стальной шар', 'Стальные шары', 'Стальных шаров', 'Стальными шарами'],
  9: ['Клык', 'Клыка', 'Клыки', 'Клыков'], 10: ['Ползун', 'Ползуна', 'Ползуны', 'Ползунов', 'Ползунами'],
  11: ['Владыка', 'Владыки', 'Владыку'], 12: ['Буревестник', 'Буревестника', 'Буревестники', 'Буревестников'],
  13: ['Кувалда', 'Кувалды', 'Кувалду', 'Кувалдами'], 14: ['Хакер', 'Хакера', 'Хакеры', 'Хакеров'],
  15: ['Светоч', 'Светоча', 'Светочи', 'Светочей'], 16: ['Феникс', 'Феникса', 'Фениксы', 'Фениксов'],
  17: ['Военный завод', 'Военного завода', 'Военные заводы'], 18: ['Призрак', 'Призрака', 'Призраки', 'Призраков'],
  19: ['Скорпион', 'Скорпиона', 'Скорпионы', 'Скорпионов'],
  20: ['Огневик', 'Огневика', 'Огневики', 'Огневиков', 'Огненный барсук', 'Огненного барсука'],
  21: ['Саблезуб', 'Саблезуба', 'Саблезубы', 'Саблезубов', 'Саблезубам'], 22: ['Тайфун', 'Тайфуна', 'Тайфуны', 'Тайфунов'],
  23: ['Песчаный червь', 'Песчаного червя', 'Песчаные черви'], 24: ['Тарантул', 'Тарантула', 'Тарантулы', 'Тарантулов'],
  25: ['Фантомный луч', 'Фантомного луча', 'Phantom Ray'], 26: ['Провидец', 'Провидца', 'Фарсир', 'Фарсира'],
  27: ['Райден', 'Райдена'], 28: ['Гончая', 'Гончей', 'Хаунд', 'Хаунда'], 29: ['Бездна', 'Бездны', 'Бездну'],
  30: ['Глаз пустоты', 'Глаза пустоты', 'Void Eye'], 31: ['Вихрь', 'Вихря', 'Вихри', 'Vortex'],
  2002: ['Гора', 'Горы', 'Mountain'],
}

const sourceUnitNames = Object.values(UNIT_RECORDS)
  .flatMap((unit) => [
    { source: unit.nameEn, id: unit.id },
    { source: unit.nameRu, id: unit.id },
    ...(RU_UNIT_ALIASES[unit.id] ?? []).map((source) => ({ source, id: unit.id })),
  ])
  .sort((a, b) => b.source.length - a.source.length)

const sourceBrandNames = (Object.entries(sourceEntities) as [EntityKind, SourceEntity[]][])
  .flatMap(([kind, rows]) => rows.flatMap((row) => {
    const override = overrideFor(kind, String(row.id), row)
    if (!override) return []
    return [
      { source: row.en, target: override }, { source: row.ru, target: override },
      { source: override.en, target: override }, { source: override.ru, target: override },
    ]
  }))
  // Single generic words can be ordinary prose; only replace unambiguous compound labels here.
  .filter((item) => /[\s-]/u.test(item.source))
  .sort((a, b) => b.source.length - a.source.length)

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Localizes known public names/tooltips without touching placeholders or numbers.
 * Exact source descriptions use the authoritative EN/RU pair; embedded legacy unit names
 * are then replaced with their MechaRulez presentation names.
 */
export function localizeText(text: string, language: Locale = locale): string {
  let output = exactText.get(text)?.[language] ?? localizeTemplate(text, language) ?? text
  const drop = output.match(/^Немедленно получите отряды (.+) \((\d+)\) ранга (\d+)$/u)
  if (drop && language === 'en') output = `Immediately receive ${drop[1]} squads (${drop[2]}) at rank ${drop[3]}`
  return replaceBrandEntityNames(replaceUnitNames(output, language), language)
}

function replaceUnitNames(text: string, language: Locale): string {
  let output = text
  for (const item of sourceUnitNames) {
    output = output.replace(new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(item.source)}(?![\\p{L}\\p{N}])`, 'giu'), unitName(item.id, language))
  }
  return output
}

function replaceBrandEntityNames(text: string, language: Locale): string {
  let output = text
  for (const item of sourceBrandNames) {
    output = output.replace(new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(item.source)}(?![\\p{L}\\p{N}])`, 'giu'), item.target[language])
  }
  return output
}
